import { useState, useEffect } from 'react'
import {
  Spinner, Alert, Form, Button, Card, Row, Col, Badge
} from 'react-bootstrap'
import client from '../api/client'

function Compare() {
  const [models, setModels] = useState([])
  const [selectedModels, setSelectedModels] = useState([])
  const [prompt, setPrompt] = useState('')
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadingModels, setLoadingModels] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    fetchModels()
  }, [])

  const fetchModels = async () => {
    try {
      const response = await client.get('/api/v1/models')
      setModels(response.data.models || [])
    } catch (err) {
      setError('Erreur lors du chargement des modèles')
      console.error(err)
    } finally {
      setLoadingModels(false)
    }
  }

  const handleModelToggle = (modelCode) => {
    if (selectedModels.includes(modelCode)) {
      setSelectedModels(selectedModels.filter(code => code !== modelCode))
    } else {
      if (selectedModels.length < 5) {
        setSelectedModels([...selectedModels, modelCode])
      } else {
        setError('Maximum 5 modèles')
        setTimeout(() => setError(''), 3000)
      }
    }
  }

  const handleCompare = async (e) => {
    e.preventDefault()
    if (selectedModels.length < 2) {
      setError('Sélectionnez au moins 2 modèles')
      return
    }
    if (!prompt.trim()) {
      setError('Entrez un prompt')
      return
    }

    setLoading(true)
    setError('')
    setResults([])

    try {
      const response = await client.post('/api/v1/compare/completions', {
        models: selectedModels,
        messages: [{ role: 'user', content: prompt }]
      })
      setResults(response.data.comparisons || [])
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la comparaison')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  if (loadingModels) {
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" />
      </div>
    )
  }

  return (
    <div>
      <h2 className="mb-4">Comparaison de Modèles</h2>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

      <Card className="mb-4">
        <Card.Body>
          <Form onSubmit={handleCompare}>
            <Form.Group className="mb-3">
              <Form.Label>Modèles à comparer (2-5)</Form.Label>
              <div className="d-flex flex-wrap gap-2">
                {models.slice(0, 20).map(model => (
                  <Form.Check
                    key={model.code}
                    type="checkbox"
                    id={`model-${model.code}`}
                    label={model.name}
                    checked={selectedModels.includes(model.code)}
                    onChange={() => handleModelToggle(model.code)}
                    disabled={!selectedModels.includes(model.code) && selectedModels.length >= 5}
                  />
                ))}
              </div>
              <Form.Text className="text-muted">
                {selectedModels.length} modèle(s) sélectionné(s)
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Prompt</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Entrez votre prompt ici..."
              />
            </Form.Group>

            <Button
              variant="primary"
              type="submit"
              disabled={loading || selectedModels.length < 2}
            >
              {loading ? <Spinner as="span" animation="border" size="sm" /> : 'Comparer'}
            </Button>
          </Form>
        </Card.Body>
      </Card>

      {results.length > 0 && (
        <div>
          <h4 className="mb-3">Résultats</h4>
          <Row>
            {results.map((result, index) => (
              <Col key={index} md={6} lg={4} className="mb-3">
                <Card className={result.error ? 'border-danger' : 'border-success'}>
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <strong>{result.model_code}</strong>
                    {result.latency_ms && (
                      <Badge bg="secondary">{result.latency_ms}ms</Badge>
                    )}
                  </Card.Header>
                  <Card.Body>
                    {result.error ? (
                      <Alert variant="danger" className="mb-0">
                        <small>{result.error}</small>
                      </Alert>
                    ) : (
                      <>
                        <div className="mb-2" style={{ whiteSpace: 'pre-wrap' }}>
                          {result.content}
                        </div>
                        {result.tokens > 0 && (
                          <Badge bg="info">{result.tokens} tokens</Badge>
                        )}
                      </>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      )}
    </div>
  )
}

export default Compare
